import type { AuditEventDto, AuditQuery } from '@fruuit/shared'
import {
  Badge,
  Button,
  Callout,
  Card,
  type Column,
  DataTable,
  DateInput,
  EmptyState,
  Input,
  PageHeader,
  Select,
} from '@fruuit/ui'
import { ScrollText } from 'lucide-react'
import { useMemo, useState } from 'react'
import { errorMessage } from '../../api/client'
import { AuditEventDrawer } from '../../features/audit/AuditEventDrawer'
import { useAuditList } from '../../features/audit/api'
import { VerifyBadge } from '../../features/audit/VerifyBadge'
import { formatDateTime, shortHash } from '../../features/records/format'
import { useDebouncedValue } from '../../features/records/useDebouncedValue'
import { useTitle } from '../../lib/useTitle'

const PAGE_SIZE = 50

interface Filters {
  chain: string
  actor_id: string
  action: string
  kind: string
  record_id: string
  from: string
  to: string
}

const EMPTY: Filters = {
  chain: '',
  actor_id: '',
  action: '',
  kind: '',
  record_id: '',
  from: '',
  to: '',
}

function actionTone(action: string) {
  if (/denied|failed|retract|revoke/.test(action)) return 'attention' as const
  if (/publish|release|approve/.test(action)) return 'positive' as const
  if (/return|impersonat/.test(action)) return 'watch' as const
  return 'info' as const
}

/** S15 Audit trail (R-08): filterable chained events, event detail and chain verification. */
export function AuditPage() {
  useTitle('Audit trail')
  const [filters, setFilters] = useState<Filters>(EMPTY)
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined])
  const [selected, setSelected] = useState<AuditEventDto | null>(null)
  const debounced = useDebouncedValue(filters, 300)
  const cursor = cursors[cursors.length - 1]
  const dateError = filters.from && filters.to && filters.from > filters.to

  const query: AuditQuery = { limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) }
  for (const [key, value] of Object.entries(debounced) as [keyof Filters, string][]) {
    if (value.trim() && !(dateError && (key === 'from' || key === 'to'))) {
      ;(query as Record<string, string>)[key] = value.trim()
    }
  }
  const list = useAuditList(query)
  const rows = list.data?.items ?? []
  const chains = list.data?.chains ?? []
  const labels = useMemo(() => new Map(chains.map((c) => [c.chain, c.label])), [chains])
  const chainLabel = (chain: string) => labels.get(chain) ?? chain
  const verifyChain =
    filters.chain || (chains.some((c) => c.chain === 'core') ? 'core' : (chains[0]?.chain ?? null))

  function update(key: keyof Filters, value: string) {
    setFilters((current) => ({ ...current, [key]: value }))
    setCursors([undefined])
  }

  const columns: Column<AuditEventDto>[] = [
    {
      id: 'when',
      header: 'Time and actor',
      cell: (row) => (
        <div className="min-w-0">
          <p className="whitespace-nowrap text-ink">{formatDateTime(row.ts)}</p>
          <p className="text-xs text-sage">
            {row.actor_name ?? row.actor_id ?? 'System'}
            {row.impersonated_by ? ' (impersonated)' : ''}
          </p>
        </div>
      ),
    },
    {
      id: 'action',
      header: 'Action',
      cell: (row) => (
        <div className="flex min-w-0 flex-col items-start gap-1">
          <Badge tone={actionTone(row.action)}>{row.action}</Badge>
          <span className="max-w-[18rem] truncate font-mono text-xs text-sage">
            {row.kind}
            {row.record_id ? ` / ${row.record_id}` : ''}
          </span>
        </div>
      ),
    },
    {
      id: 'chain',
      header: 'Chain',
      cell: (row) => (
        <span className="text-sm whitespace-nowrap text-ink-soft">
          {chainLabel(row.chain)} · #{row.seq}
        </span>
      ),
    },
    {
      id: 'hash',
      header: 'Hash',
      cell: (row) => (
        <span className="font-mono text-xs text-sage" title={row.hash}>
          {shortHash(row.hash, 16)}
        </span>
      ),
    },
    {
      id: 'inspect',
      header: <span className="sr-only">Inspect</span>,
      align: 'right',
      cell: (row) => (
        <Button size="sm" variant="secondary" onClick={() => setSelected(row)}>
          Inspect
        </Button>
      ),
    },
  ]

  const filtered = Object.values(filters).some((value) => value.trim())

  return (
    <>
      <PageHeader
        eyebrow="Accountable records"
        title="The history survives the change."
        description="Append-only events capture the actor, the action, the before and after record and a chained integrity hash."
      />
      <Card className="mb-6" padding="sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-semibold text-ink">
              Chain integrity · {verifyChain ? chainLabel(verifyChain) : 'no chain'}
            </p>
            <p className="text-xs text-sage">
              Recomputes every hash in the chain on the server. A nightly job does the same and
              anchors the chain heads outside the database.
            </p>
          </div>
          <VerifyBadge chain={verifyChain} />
        </div>
      </Card>
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Select
          label="Chain"
          value={filters.chain}
          placeholder="All chains you can read"
          options={chains.map((c) => ({ value: c.chain, label: c.label }))}
          onChange={(event) => update('chain', event.target.value)}
        />
        <Input
          label="Action"
          placeholder="e.g. assessments.publish"
          value={filters.action}
          onChange={(event) => update('action', event.target.value)}
        />
        <Input
          label="Record kind"
          placeholder="e.g. assessments"
          value={filters.kind}
          onChange={(event) => update('kind', event.target.value)}
        />
        <Input
          label="Record id"
          value={filters.record_id}
          onChange={(event) => update('record_id', event.target.value)}
        />
        <Input
          label="Actor id"
          value={filters.actor_id}
          onChange={(event) => update('actor_id', event.target.value)}
        />
        <DateInput
          label="From"
          value={filters.from}
          onChange={(event) => update('from', event.target.value)}
        />
        <DateInput
          label="To"
          value={filters.to}
          error={dateError ? 'The start date must not follow the end date.' : undefined}
          onChange={(event) => update('to', event.target.value)}
        />
        <div className="flex items-end">
          <Button
            variant="ghost"
            disabled={!filtered}
            onClick={() => {
              setFilters(EMPTY)
              setCursors([undefined])
            }}
          >
            Clear filters
          </Button>
        </div>
      </div>
      {list.isError ? (
        <Callout tone="danger" role="alert" className="mb-4">
          {errorMessage(list.error)}
        </Callout>
      ) : null}
      <DataTable
        caption="Audit events, newest first"
        columns={columns}
        rows={rows}
        getRowId={(row) => `${row.chain}:${row.seq}`}
        loading={list.isLoading}
        onRowClick={(row) => setSelected(row)}
        empty={
          <EmptyState
            variant="plain"
            icon={<ScrollText />}
            title={filtered ? 'No events match these filters' : 'No audit events yet'}
            description={
              filtered
                ? 'Change or clear the filters to see more events.'
                : 'Every change and review decision will appear here.'
            }
          />
        }
        pagination={{
          mode: 'server',
          hasPrevious: cursors.length > 1,
          hasNext: Boolean(list.data?.next_cursor),
          onPrevious: () => setCursors((current) => current.slice(0, -1)),
          onNext: () => {
            const next = list.data?.next_cursor
            if (next) setCursors((current) => [...current, next])
          },
          summary: `${rows.length} events · newest first · page ${cursors.length}`,
        }}
      />
      <AuditEventDrawer
        event={selected}
        chainLabel={chainLabel}
        onClose={() => setSelected(null)}
      />
    </>
  )
}
